/**
 * PerformanceTiles — one tile per period, return first
 * ====================================================
 * Usability review of 23 October 2026, item 17: *"Lead with the return %,
 * put the dollar change underneath, and move the income/capital split into a
 * hover breakdown. The 3Y and Since Inception tiles are identical; hide one
 * when they match."*
 *
 * The figure that leads is an estimated return, not the change in net assets
 * as a percentage: the change has capital in and out in it (CLAUDE.md §0.31),
 * and on this book that is most of it. Where the estimate cannot be made the
 * tile says n/a and the breakdown says why in figures (§0.30).
 */
import React, { useId, useState } from "react";
import { InfoTip } from "../../components/PageTools";
import { day, money, pct, toNumber } from "../../components/format";
import { WINDOW_TILES, sameWindow, windowReturn } from "./navLogic";

const whole = (v) => money(v, { whole: true });
const signed = (v) => (v > 0 ? `+${whole(v)}` : whole(v));
const tone = (v) => (v === null || v === undefined || v === 0 ? "" : v > 0 ? " nvp-pos" : " nvp-neg");

function Breakdown({ w, result, label }) {
  const split = result.basis === "split";
  const rows = [
    [`Opening net assets${w.opens_on ? ` (${day(w.opens_on)})` : " (inception)"}`, w.opening_usd],
    ["Profit", w.net_income_usd],
    ...(split
      ? [["Capital in less capital out", w.capital_usd], ["Currency translation", w.currency_translation_usd]]
      : [["Capital in less out, and currency moves", w.capital_and_other_usd]]),
    ["Closing net assets", w.closing_usd],
  ];
  return (
    <div className="nvp-breakdown">
      <div className="nvp-breakdown-title">{label}: what moved net assets</div>
      <table>
        <tbody>
          {rows.map(([name, value], i) => (
            <tr key={name} className={i === rows.length - 1 ? "nvp-breakdown-total" : ""}>
              <th scope="row">{name}</th>
              <td>{whole(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        <b>Return (estimate)</b>{" "}
        {result.value === null
          ? `n/a. ${result.reason}`
          : `${pct(result.value)}: a gain of ${whole(result.gain)} on average capital employed of ${whole(result.average)}.`}
      </p>
      <p>
        {split
          ? "Gain is profit plus currency translation. Average capital employed is opening net assets plus half of capital in less out."
          : "Gain is profit. Average capital employed is opening net assets plus half of capital in less out, with currency moves inside that line."}{" "}
        It is an estimate on the ledger’s figures, which hold investments at cost with no market mark.
      </p>
    </div>
  );
}

function Tile({ id, label, w, note }) {
  const [open, setOpen] = useState(false);
  const popId = useId();
  const result = windowReturn(w);
  const change = toNumber(w.change_usd);
  return (
    <div
      className="nvp-tile"
      tabIndex={0}
      role="group"
      aria-label={`${label} performance`}
      aria-describedby={open ? popId : undefined}
      data-window={id}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <div className="nvp-tile-label">{label}</div>
      <div className="nvp-tile-caption">Return (estimate)</div>
      <strong className={`nvp-tile-return${tone(result.value)}`}>
        {result.value === null ? "n/a" : pct(result.value)}
      </strong>
      {result.value === null && result.short && <div className="nvp-tile-why">{result.short}</div>}
      <div className="nvp-tile-change">
        Net assets <b className={tone(change).trim()}>{change === null ? "n/a" : signed(change)}</b>
      </div>
      {note && <div className="nvp-tile-note">{note}</div>}
      {open && (
        <div id={popId} role="tooltip" className="nvp-tile-pop">
          <Breakdown w={w} result={result} label={label} />
        </div>
      )}
    </div>
  );
}

export default function PerformanceTiles({ windows, loading, error }) {
  const hideThreeYear = windows && sameWindow(windows.three_year, windows.itd);
  const tiles = WINDOW_TILES.filter(([key]) => windows?.[key] && !(hideThreeYear && key === "three_year"));
  return (
    <section className="nvp-panel nvp-performance" aria-labelledby="nvp-performance-h">
      <div className="nvp-panel-head">
        <h2 id="nvp-performance-h">
          Performance{" "}
          <InfoTip wide label="How performance is measured">
            <p>
              Each period starts from the fund’s net assets on the general ledger: what it holds at cost, less what it
              owes. That record goes back to the first entry; market prices do not.
            </p>
            <p>
              A rise in net assets is not a return, because money paid in and out moves it too. So each tile leads with an
              estimated return that takes capital in and out out of the change. Hover a tile for its breakdown.
            </p>
          </InfoTip>
        </h2>
        <p>Hover or focus a tile to see what moved net assets.</p>
      </div>
      {error && !windows ? (
        <p className="nvp-empty">Could not load performance. {error}</p>
      ) : !windows ? (
        <div className="nvp-tiles">{loading ? <p className="nvp-empty">Loading performance…</p> : null}</div>
      ) : (
        <div className="nvp-tiles">
          {tiles.map(([key, label]) => (
            <Tile
              key={key}
              id={key}
              label={label}
              w={windows[key]}
              note={key === "itd" && hideThreeYear ? "Last 3 years is the same, so it is not shown twice." : null}
            />
          ))}
        </div>
      )}
    </section>
  );
}
